import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getUserData } from '../../services/API';
import { PhraseTriggers } from '../../constants/constants';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { getCopyByGradeband } from '../../services/ThemingService';
import { logSessionInfo } from '../../services/logger';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import FreeAnswerMediaSection from './FreeAnswerMediaSection';
import FreeAnswerRepeatButton from './FreeAnswerRepeatButton';
import FreeAnswerMic from './FreeAnswerMic';
import FreeAnswerSubmitButton from './FreeAnswerSubmitButton';
import { useContainerHeightVar } from './freeAnswerScaling';
import {
  SUBMIT_TIMER_TICK_MS,
  RECORDING_ACTIVITY_TYPE,
  getMinimumSubmitWaitTime,
  getMaxNoSubmitTime,
  LOG,
} from './freeAnswerConstants';

const FreeAnswer = ({
  speech,
  imageURL,
  videoURL,
  isInstruction,
  isRepeating,
  onDone,
  storyId,
  activityId,
  phraseIndex,
  setRecordingProps,
  shouldUseAOS,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  onImageLoadError,
  setHideParentListeningStatus,
  setHideParentRepeatButton,
  handleRepeatInstructions,
  repeatInstructions,
  postStimulusQuestion,
  currentIntroductionTrigger,
}) => {
  const containerRef = useRef(null);
  useContainerHeightVar(containerRef);

  const hasVideo = !!videoURL;
  const hasImage = !!imageURL && !hasVideo;

  const [isCapturing, setIsCapturing] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoFinished, setVideoFinished] = useState(!hasVideo);

  const isMountedRef = useIsMounted();
  const submitTimerIntervalRef = useRef(null);
  const submitTimerStartedAtRef = useRef(null);
  const submitTimerElapsedRef = useRef(0);
  const submissionDispatchedRef = useRef(false);
  const captureStartedRef = useRef(false);
  const videoStartedRef = useRef(false);
  const questionAskedRef = useRef(false);

  const speechRef = useRef(speech);
  speechRef.current = speech;
  const setRecordingPropsRef = useRef(setRecordingProps);
  setRecordingPropsRef.current = setRecordingProps;
  const setHideParentListeningStatusRef = useRef(setHideParentListeningStatus);
  setHideParentListeningStatusRef.current = setHideParentListeningStatus;
  const setHideParentRepeatButtonRef = useRef(setHideParentRepeatButton);
  setHideParentRepeatButtonRef.current = setHideParentRepeatButton;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const isSpeaking = !!speech.isSpeaking;
  const isSpeechListening = !!speech.isListening;

  const clearSubmitTimer = useCallback(() => {
    if (submitTimerIntervalRef.current) {
      clearInterval(submitTimerIntervalRef.current);
      submitTimerIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    speechRef.current.stopListening();
    setHideParentListeningStatusRef.current(true);
    setHideParentRepeatButtonRef.current(true);

    return () => {
      clearSubmitTimer();
      speechRef.current.stopListening();
      setHideParentListeningStatusRef.current(false);
      setHideParentRepeatButtonRef.current(false);
    };
  }, [clearSubmitTimer]);

  const handleSubmit = useCallback(() => {
    if (submissionDispatchedRef.current) return;
    submissionDispatchedRef.current = true;

    clearSubmitTimer();
    setIsCapturing(false);
    setSubmitEnabled(false);
    speechRef.current.stopListening();

    logSessionInfo('free_answer_submit', {
      activityId,
      storyId,
      phraseIndex,
      elapsedMs: submitTimerElapsedRef.current,
    });

    speechRef.current.speakWithPromise({
      textToSpeak: getCopyByGradeband(
        getCopyByGradeband(COPY_STRINGS, 'freeAnswer'),
        'submit_confirmation'
      ),
      useGenerative: true,
    }).finally(() => {
      if (!isMountedRef.current) return;
      if (onDoneRef.current) onDoneRef.current();
    });
  }, [activityId, storyId, phraseIndex, clearSubmitTimer, isMountedRef]);

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  const startSubmitTimer = useCallback(() => {
    clearSubmitTimer();
    submitTimerElapsedRef.current = 0;
    submitTimerStartedAtRef.current = Date.now();

    const minimumWait = getMinimumSubmitWaitTime();
    const maxNoSubmit = getMaxNoSubmitTime(shouldExtendTimeout, timeoutExtensionMultiplier);

    submitTimerIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - submitTimerStartedAtRef.current;
      submitTimerElapsedRef.current = elapsed;

      if (elapsed > maxNoSubmit) {
        clearSubmitTimer();
        handleSubmitRef.current();
        return;
      }
      if (elapsed >= minimumWait) {
        setSubmitEnabled(true);
      }
    }, SUBMIT_TIMER_TICK_MS);
  }, [shouldExtendTimeout, timeoutExtensionMultiplier, clearSubmitTimer]);

  const startCapture = useCallback(() => {
    setRecordingPropsRef.current({
      activityId,
      storyId,
      phraseIndex,
      activityType: RECORDING_ACTIVITY_TYPE,
      studentId: getUserData().userId,
      clearHistory: true,
    });

    speechRef.current.setForceAOS(shouldUseAOS, () => {
      // setForceAOS defers this through setState, so the item can unmount inside the
      // window. The cleanup effect has already stopped listening and cleared the timer by
      // then, and opening the mic again here would start an interval nothing clears.
      if (!isMountedRef.current) return;
      speechRef.current.startListening(null, null, false, false, null, false, false);
      setIsCapturing(true);
      startSubmitTimer();
    });
  }, [activityId, storyId, phraseIndex, shouldUseAOS, startSubmitTimer, isMountedRef]);

  const resetAnswerTurn = useCallback(() => {
    clearSubmitTimer();
    submitTimerElapsedRef.current = 0;
    submitTimerStartedAtRef.current = null;
    captureStartedRef.current = false;
    setIsCapturing(false);
    setSubmitEnabled(false);
    speechRef.current.stopListening();
  }, [clearSubmitTimer]);

  const beginAnswerTurn = useCallback(() => {
    if (captureStartedRef.current) return;
    captureStartedRef.current = true;

    LOG('beginAnswerTurn', { activityId, storyId, phraseIndex });
    speechRef.current.abortSpeech(() => {
      if (!isMountedRef.current) return;
      speechRef.current.stopListening();

      if (questionAskedRef.current || !postStimulusQuestion) {
        startCapture();
        return;
      }

      questionAskedRef.current = true;
      speechRef.current.speakWithPromise({
        textToSpeak: postStimulusQuestion,
        useGenerative: true,
      }).finally(() => {
        if (!isMountedRef.current) return;
        startCapture();
      });
    });
  }, [startCapture, postStimulusQuestion, activityId, storyId, phraseIndex, isMountedRef]);

  const startStimulusVideo = useCallback(() => {
    if (videoStartedRef.current) return;
    videoStartedRef.current = true;

    LOG('startStimulusVideo', { activityId, storyId, phraseIndex });
    speechRef.current.abortSpeech(() => {
      if (!isMountedRef.current) return;
      speechRef.current.stopListening();
      setVideoPlaying(true);
    });
  }, [activityId, storyId, phraseIndex, isMountedRef]);

  useEffect(() => {
    if (isInstruction || isRepeating || isSpeaking) return;
    if (hasVideo && !videoFinished) {
      startStimulusVideo();
      return;
    }
    beginAnswerTurn();
  }, [
    isInstruction, isRepeating, isSpeaking, hasVideo, videoFinished,
    startStimulusVideo, beginAnswerTurn,
  ]);

  useEffect(() => {
    if (captureStartedRef.current || !isSpeechListening) return;
    speechRef.current.stopListening();
  }, [isSpeechListening]);

  useEffect(() => {
    if (!isRepeating) return;
    videoStartedRef.current = true;
    resetAnswerTurn();
  }, [isRepeating, resetAnswerTurn]);

  const onVideoStart = useCallback(() => {
    setVideoFinished(false);
    resetAnswerTurn();
  }, [resetAnswerTurn]);

  const onVideoDone = useCallback(() => {
    setVideoPlaying(false);
    setVideoFinished(true);
  }, []);

  const videoInProgress = hasVideo && !videoFinished;
  const interactionLocked = videoInProgress || isRepeating || isSpeaking;

  const hasRepeatToPlay =
    !!handleRepeatInstructions && String(repeatInstructions || '').trim() !== '';

  return (
    <div className="freeAnswer" ref={containerRef}>
      {hasRepeatToPlay && (
        <FreeAnswerRepeatButton
          disabled={interactionLocked || !isCapturing}
          onRepeat={handleRepeatInstructions}
          showPointer={currentIntroductionTrigger === PhraseTriggers.Repeat}
        />
      )}
      <FreeAnswerMediaSection
        hasVideo={hasVideo}
        hasImage={hasImage}
        videoURL={videoURL}
        imageURL={imageURL}
        speech={speech}
        videoPlaying={videoPlaying}
        unmountVideoWhenDone={getUserData().sessionData.isiPad}
        onVideoStart={onVideoStart}
        onVideoDone={onVideoDone}
        onImageLoadError={onImageLoadError}
      />
      <FreeAnswerMic volumeLevel={speech.volumeLevel} />
      <FreeAnswerSubmitButton
        enabled={submitEnabled && !interactionLocked}
        onSubmit={handleSubmit}
        showPointer={currentIntroductionTrigger === PhraseTriggers.Submit}
      />
    </div>
  );
};

export default FreeAnswer;
